import { useContext, useEffect } from 'react';
import { OpenLayerObserverContext } from './open-layer-observer-context';
import { useOpenLayerObserverNamespace } from './use-open-layer-observer-namespace';
/**
 * Hook that increments/decrements the open layer count when the component mounts/unmounts or becomes visible/hidden.
 * It is used to "notify" the layer observer(s) that a new layer has been added/opened.
 *
 * It takes an object with two arguments:
 * - `isOpen` - used to conditionally update the layer count based on the visibility of the layered component.
 * - `onClose` - callback used to close this layer when the OpenLayerObserver has called `closeLayers`.
 *
 * Example usage:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false); // State for controlling layer visibility
 * useNotifyLayerObserver({
 *   isOpen,
 *   onClose: () => setIsOpen(false) // Optional callback to close this layer
 * });
 * ```
 */
export function useNotifyOpenLayerObserver(_ref) {
  var isOpen = _ref.isOpen,
    onClose = _ref.onClose;
  var context = useContext(OpenLayerObserverContext);
  var namespace = useOpenLayerObserverNamespace();
  useEffect(function () {
    /**
     * Registers the `onClose` callback with the OpenLayerObserver.
     */

    if (context === null) {
      return;
    }
    if (!isOpen) {
      /**
       * If the layer is not open, we are not registering the `onClose` callback.
       * This is important to prevent the `onClose` from being called for layers that
       * are not open.
       *
       * Some consumers mistakenly pass "toggle" functions to `onClose` callbacks for
       * layer components, as Popup, e.g. `onClose={() => setIsOpen(!isOpen)}`.
       */
      return;
    }
    return context.onClose(onClose, {
      namespace: namespace
    });
  }, [context, isOpen, namespace, onClose]);
}